import { Badge, Box, Spinner, Table, Text, VStack } from "components/ui";
import { useGatewayConfigChanges } from "hooks/useAiTraces";
import { useTranslation } from "hooks/useTranslation";
import { GatewayConfigAuditEntry } from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

interface ConfigChangesPanelProps {
  /** Bumped by the page's Refresh button. Re-reads the history without remounting the panel. */
  refreshToken: number;
}

const formatDateTime = (iso: string) => new Date(iso).toLocaleString();

/**
 * Who changed what in the gateway configuration — connectors, route bindings, difficulty profile.
 * Sits under Current setup so the effective configuration and its history are read together.
 */
const ConfigChangesPanel = ({ refreshToken }: ConfigChangesPanelProps) => {
  const { t } = useTranslation();
  const { configChanges, loading } = useGatewayConfigChanges(refreshToken);

  const renderConfigChange = (entry: GatewayConfigAuditEntry) => (
    <Table.Row key={entry.id}>
      <Table.Cell>
        <Text fontSize="sm">{formatDateTime(entry.createdAt)}</Text>
      </Table.Cell>
      <Table.Cell>
        <VStack align="start" gap={0}>
          <Text fontSize="sm">{entry.entityType}</Text>
          {entry.entityName && (
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {entry.entityName}
            </Text>
          )}
        </VStack>
      </Table.Cell>
      <Table.Cell>
        <Badge colorPalette="gray" variant="subtle">
          {entry.action}
        </Badge>
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="sm">
          {entry.actorEmail ?? t("app.aiGateway.traces.changes.systemActor")}
        </Text>
      </Table.Cell>
    </Table.Row>
  );

  return (
    <VStack align="stretch" gap={2}>
      <Text fontSize="sm" fontWeight="semibold">
        {t("app.aiGateway.traces.changes.title")}
      </Text>
      <Text fontSize="sm" color={getThemeColor("text.secondary")}>
        {t("app.aiGateway.traces.changes.description")}
      </Text>
      {loading && configChanges.length === 0 ? (
        <Spinner size="sm" />
      ) : configChanges.length === 0 ? (
        <Text fontSize="sm" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.traces.changes.empty")}
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline" minW="620px">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {t("app.aiGateway.traces.changes.when")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.traces.changes.entity")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.traces.changes.action")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.traces.changes.actor")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>{configChanges.map(renderConfigChange)}</Table.Body>
          </Table.Root>
        </Box>
      )}
    </VStack>
  );
};

export default ConfigChangesPanel;
